import styled, { css } from 'styled-components';

// ---------------------------------------------------------------------------
// Dashboard "Vendas a prazo" — primitivos da tela (handoff de design 0084).
//
// Tradução do protótipo HTML para styled-components: nenhum estilo inline veio
// junto. Os tokens saem de `config/theme.js`; onde o design fixa um valor que
// ainda não é token (as cores dos buckets de aging, por exemplo), o literal fica
// aqui, num lugar só, comentado — não espalhado pelos componentes.
//
// Geometria do design: raio 10 em controles, 12 em cards, 999 em pills; altura
// 40 em inputs/botões de ação e 34 em chips/segmented; sombra única
// `0 1px 2px rgba(0,0,0,0.04)`; transições só de cor/borda, 120–160ms.
//
// Sobre os botões do cabeçalho: são controles de SHELL ESCURO, caso que a
// FC7 §G.2 mantém explicitamente local (o kit `styles/ui/Button.js` é para
// superfície clara). Por isso vivem aqui, e não como variante nova do kit.
// ---------------------------------------------------------------------------

const CARD_BORDER = 'rgba(0, 0, 0, 0.08)';
const CONTROL_BORDER = 'rgba(0, 0, 0, 0.12)';
const CARD_SHADOW = '0 1px 2px rgba(0, 0, 0, 0.04)';
const FOCUS_RING = '0 0 0 3px rgba(64, 152, 239, 0.18)';

// Números NUNCA quebram linha e usam algarismo de largura fixa: coluna de valor
// que "dança" entre linhas é ilegível para conferência (0084 §5.9).
export const numericCss = css`
   font-variant-numeric: tabular-nums;
   white-space: nowrap;
`;

const focusableCss = css`
   &:focus-visible {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: ${FOCUS_RING};
   }
`;

// ─── Cabeçalho sobre o shell escuro ───────────────────────────────────────

export const ShellButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 7px;
   height: 40px;
   padding: 0 14px;
   border-radius: 10px;
   border: 1px solid rgba(255, 255, 255, 0.16);
   background: transparent;
   color: ${({ theme }) => theme?.colors?.textOnAppBackground ?? '#e5e5e5'};
   font-size: 13px;
   font-weight: 600;
   white-space: nowrap;
   cursor: pointer;
   transition: background 140ms ease, border-color 140ms ease;

   &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.08);
   }

   &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.24);
   }

   svg {
      flex-shrink: 0;
   }
`;

export const ShellPrimaryButton = styled(ShellButton)`
   border-color: transparent;
   background: ${({ theme }) => theme?.colors?.primarySoft ?? '#96c4f0'};
   color: #0b3a63;
   font-size: 14px;
   font-weight: 700;
   box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);

   &:hover:not(:disabled) {
      background: #8bbcee;
   }
`;

// ─── Cards ────────────────────────────────────────────────────────────────

export const Card = styled.section`
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   border: 1px solid ${CARD_BORDER};
   border-radius: 12px;
   box-shadow: ${CARD_SHADOW};
   padding: ${({ $pad }) => $pad ?? '16px'};
`;

export const CardHead = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
   margin-bottom: 14px;
`;

export const CardTitle = styled.h2`
   margin: 0;
   font-size: 15px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const CardSubtitle = styled.p`
   margin: 4px 0 0;
   font-size: 12px;
   line-height: 1.5;
   color: #8a8a8a;
`;

export const CardNote = styled.span`
   font-size: 12px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

// Pilha vertical das dobras.
export const Stack = styled.div`
   display: grid;
   gap: 14px;
`;

// ─── Pills ────────────────────────────────────────────────────────────────

export const Pill = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 5px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   background: ${({ $tone }) =>
      $tone === 'info' ? 'rgba(2, 136, 209, 0.1)' : 'rgba(0, 0, 0, 0.06)'};
   border: 1px solid
      ${({ $tone }) =>
         $tone === 'info' ? 'rgba(2, 136, 209, 0.3)' : 'rgba(0, 0, 0, 0.12)'};
   color: ${({ $tone }) => ($tone === 'info' ? '#01579b' : '#444')};
`;

// ─── Painel de qualidade dos dados ────────────────────────────────────────

export const QualityPills = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin-bottom: 12px;
`;

export const QualityNotes = styled.ul`
   margin: 0;
   padding-left: 18px;
   display: grid;
   gap: 4px;

   li {
      font-size: 12px;
      line-height: 1.65;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
`;

// ─── Barra de filtros ─────────────────────────────────────────────────────

export const FilterCard = styled(Card)`
   padding: 0;
`;

export const FilterRow = styled.div`
   display: flex;
   align-items: flex-end;
   flex-wrap: wrap;
   gap: 16px;
   padding: 14px 16px;

   & + & {
      border-top: 1px solid rgba(0, 0, 0, 0.07);
   }
`;

export const FilterGroup = styled.div`
   display: grid;
   gap: 7px;
   min-width: 0;
   ${({ $grow }) => ($grow ? 'flex: 1 1 320px;' : '')}
   ${({ $right }) => ($right ? 'margin-left: auto;' : '')}
`;

export const FilterLabel = styled.span`
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.06em;
   text-transform: uppercase;
   color: #8a8a8a;
`;

export const ChipRow = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   align-items: center;
`;

export const Chip = styled.button`
   height: 34px;
   padding: 0 14px;
   border-radius: 999px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   transition: background 140ms ease, border-color 140ms ease;
   background: ${({ $active, theme }) =>
      $active ? theme?.colors?.appBackground ?? '#373737' : '#fff'};
   color: ${({ $active }) => ($active ? '#fff' : '#444')};
   border: 1px solid
      ${({ $active, theme }) =>
         $active ? theme?.colors?.appBackground ?? '#373737' : CONTROL_BORDER};

   /* ⚠️ O background PRECISA ser repetido aqui. O GlobalStyles tem uma regra
      button:hover com background-color — especificidade 0,0,1,1, que vence a
      classe do styled-component (0,0,1,0). Sem esta regra, o chip pisca no
      azul do legado ao passar o mouse. Vale para todo styled.button da tela. */
   &:hover:not(:disabled) {
      background: ${({ $active, theme }) =>
         $active ? theme?.colors?.appBackground ?? '#373737' : '#f3f8fe'};
      border-color: ${({ $active }) =>
         $active ? 'transparent' : 'rgba(0, 0, 0, 0.24)'};
   }

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }

   ${focusableCss}
`;

// ─── Abas da tela (sobre o shell escuro) ──────────────────────────────────
//
// Mesma anatomia do `TabButton` do kit (sublinhado na cor de ação), com as
// cores do shell — o kit é para superfície clara e o texto sumiria aqui. É o
// caso que a FC7 §G.2 mantém deliberadamente local.
export const SectionTabs = styled.nav`
   display: flex;
   flex-wrap: wrap;
   gap: 4px;
   border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

export const SectionTab = styled.button`
   appearance: none;
   border: 0;
   background: none;
   cursor: pointer;
   font-family: inherit;
   padding: 10px 14px;
   font-size: 13.5px;
   font-weight: 700;
   color: ${({ $active, theme }) =>
      $active ? '#fff' : theme?.colors?.textMutedOnAppBackground ?? '#bfbfbf'};
   border-bottom: 3px solid
      ${({ $active, theme }) =>
         $active ? theme?.colors?.primary ?? '#4098ef' : 'transparent'};
   margin-bottom: -1px;
   transition: color 130ms ease, border-color 130ms ease;

   /* background explícito neutraliza a regra button:hover do GlobalStyles,
      que pintaria a aba de azul sólido ao passar o mouse. */
   &:hover:not(:disabled) {
      background: none;
      color: #fff;
   }

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.24);
   }
`;

// Mesma geometria do Chip, invertida para o fundo escuro do shell (usada na
// barra de período fixa). Não é variante do kit: é controle de shell escuro, o
// caso que a FC7 §G.2 mantém local.
export const ShellChip = styled.button`
   height: 30px;
   padding: 0 13px;
   border-radius: 999px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   transition: background 140ms ease, border-color 140ms ease;
   background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
   color: ${({ $active, theme }) =>
      $active ? theme?.colors?.appBackground ?? '#373737' : '#e5e5e5'};
   border: 1px solid
      ${({ $active }) => ($active ? '#fff' : 'rgba(255, 255, 255, 0.22)')};

   &:hover:not(:disabled) {
      background: ${({ $active }) =>
         $active ? '#fff' : 'rgba(255, 255, 255, 0.08)'};
   }

   &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.24);
   }
`;

// Barra de período que desce ao rolar, depois que a barra de filtros sai da
// tela. Existe para o operador não perder de vista QUAL recorte ele está lendo
// — e poder trocá-lo sem voltar ao topo.
//
// Abaixo de 900px ela não aparece: nessa largura o Topbar do app já ocupa o
// topo da viewport (sticky), e empilhar duas barras fixas num celular come a
// tela e cobriria o botão do menu.
// `left` e `width` chegam INLINE, medidos da barra de filtros real. Fixar
// `left: 0; right: 0` faria a barra atravessar o sidebar — e como o menu
// colapsa, não há valor de offset que sirva sempre. Medir o elemento resolve os
// dois casos e acompanha o colapso sem a página saber que ele existe.
export const StickyPeriodBar = styled.div`
   position: fixed;
   top: 0;
   z-index: 9;
   display: flex;
   align-items: center;
   gap: 14px;
   flex-wrap: wrap;
   padding: 10px 16px;
   background: ${({ theme }) => theme?.colors?.appBackground ?? '#373737'};
   border: 1px solid rgba(255, 255, 255, 0.1);
   border-top: 0;
   border-radius: 0 0 12px 12px;
   box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
   transform: translateY(${({ $visible }) => ($visible ? '0' : '-110%')});
   transition: transform 220ms ease;

   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }

   @media (max-width: 900px) {
      display: none;
   }
`;

export const StickyLabel = styled.span`
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.06em;
   text-transform: uppercase;
   color: ${({ theme }) => theme?.colors?.textMutedOnAppBackground ?? '#bfbfbf'};
`;

export const StickyRange = styled.span`
   margin-left: auto;
   font-size: 12px;
   color: ${({ theme }) => theme?.colors?.textMutedOnAppBackground ?? '#bfbfbf'};
   ${numericCss}
`;

export const DateField = styled.div`
   display: grid;
   gap: 5px;
`;

export const DateInput = styled.input`
   height: 40px;
   padding: 0 10px;
   border-radius: 10px;
   border: 1px solid
      ${({ $invalid, theme }) =>
         $invalid ? theme?.colors?.danger ?? '#ab2020' : CONTROL_BORDER};
   background: #fff;
   font-size: 13px;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   ${numericCss}
   ${focusableCss}
`;

export const FieldError = styled.span`
   font-size: 11px;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.danger ?? '#ab2020'};
`;

export const FilterHint = styled.span`
   font-size: 12px;
   color: #8a8a8a;
`;

// ─── Segmented (lente de vencimento, abas, limiar de safra) ───────────────

export const Segmented = styled.div`
   display: inline-flex;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 10px;
   overflow: hidden;
   background: #fff;
`;

export const SegmentedButton = styled.button`
   display: inline-flex;
   align-items: center;
   height: 34px;
   padding: 0 14px;
   border: 0;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   transition: background 140ms ease, color 140ms ease;
   background: ${({ $active, theme }) =>
      $active ? theme?.colors?.appBackground ?? '#373737' : '#fff'};
   color: ${({ $active }) => ($active ? '#fff' : '#666')};

   & + & {
      border-left: 1px solid ${CONTROL_BORDER};
   }

   /* Neutraliza a regra button:hover do GlobalStyles (ver nota no Chip). */
   &:hover:not(:disabled) {
      background: ${({ $active, theme }) =>
         $active ? theme?.colors?.appBackground ?? '#373737' : '#f3f8fe'};
   }

   &:focus-visible {
      outline: none;
      box-shadow: inset ${FOCUS_RING};
   }
`;

// Afordância de ajuda ao lado do RÓTULO do grupo, não dentro dos botões.
//
// Duas razões, e as duas custaram uma rodada:
//  1. Responsabilidade única — o botão existe para trocar a lente. Uma segunda
//     afordância dentro dele faz o hover de ajuda competir com a área de
//     clique da ação, e o operador não sabe mais o que está mirando.
//  2. O Segmented tem overflow: hidden (é o que arredonda as pontas do
//     controle). Bolha ancorada DENTRO de um dos botões cai inteiramente fora
//     daquela caixa e é recortada — invisível, por mais correto que o seletor
//     esteja. Foi o que aconteceu.
//
// Como a explicação é do GRUPO ("o que essa lente faz?"), uma bolha só,
// descrevendo as duas opções, diz mais que duas bolhas separadas.
export const InfoHint = styled.span`
   position: relative;
   display: inline-flex;
   align-items: center;
   color: #a0a0a0;
   cursor: help;

   &:hover {
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }

   /* Focável por teclado: quem navega sem mouse não pode ficar sem a
      explicação de um controle que muda o significado de três seções. */
   &:focus-visible {
      outline: none;
      color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
      border-radius: 4px;
   }
`;

export const InfoBubble = styled.span`
   position: absolute;
   /* Ancorada à DIREITA do (i), crescendo para dentro da tela.
      Ancorar à esquerda jogava os 310px de largura para fora da viewport: o
      (i) mora na ponta direita da barra de filtros, então a bolha abria no
      vazio, criava scroll horizontal na PÁGINA INTEIRA e o scroll arrastava o
      layout do shell junto (o botão de recolher o menu descolava do sidebar).
      Regra geral: tooltip perto de uma borda cresce para o lado oposto a ela. */
   right: 0;
   /* Abre para BAIXO: o rótulo fica colado no topo do card de filtros, e uma
      bolha para cima sairia da tela. */
   top: calc(100% + 7px);
   z-index: 4;
   width: 310px;
   padding: 10px 12px;
   border-radius: 10px;
   background: rgba(31, 31, 24, 0.96);
   color: rgba(255, 255, 255, 0.82);
   font-size: 11.5px;
   font-weight: 500;
   line-height: 1.5;
   /* O rótulo do grupo é caixa-alta com letter-spacing; a bolha é texto
      corrido e precisa desfazer os dois. */
   letter-spacing: normal;
   text-transform: none;
   text-align: left;
   white-space: normal;
   box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
   pointer-events: none;
   opacity: 0;
   visibility: hidden;
   transition: opacity 140ms ease, visibility 140ms ease;

   /* A bolha é FILHA do InfoHint — o seletor é de descendência. */
   ${InfoHint}:hover &,
   ${InfoHint}:focus-visible & {
      opacity: 1;
      visibility: visible;
   }

   strong {
      color: #fff;
      font-weight: 800;
   }

   p {
      margin: 0 0 7px;
   }

   p:last-child {
      margin-bottom: 0;
      color: rgba(255, 255, 255, 0.62);
   }
`;

// Rótulo do grupo de filtro + a afordância de ajuda, na mesma linha.
export const FilterLabelRow = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
`;

// ─── Banner de cobertura de origem ────────────────────────────────────────

export const CoverageStrip = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 12px;
   padding: 14px 16px;
   border-radius: 12px;
   background: rgba(255, 193, 7, 0.14);
   border: 1px solid rgba(255, 193, 7, 0.45);

   svg {
      flex-shrink: 0;
      color: #f2af29;
   }
`;

export const CoverageBody = styled.div`
   flex: 1 1 auto;
   min-width: 0;
`;

export const CoverageTitle = styled.p`
   margin: 0;
   font-size: 14px;
   font-weight: 700;
   color: #fff;
`;

export const CoverageText = styled.p`
   margin: 4px 0 0;
   font-size: 12px;
   line-height: 1.55;
   color: #e8d5aa;
`;

// Botão claro sobre o shell — mesma família do ShellButton, porte menor.
export const CoverageAction = styled(ShellButton)`
   height: 34px;
   font-size: 12px;
   border-color: rgba(255, 255, 255, 0.3);
`;

// ─── Empty state ──────────────────────────────────────────────────────────

export const EmptyCard = styled(Card)`
   padding: 56px 24px;
   text-align: center;
   display: grid;
   gap: 10px;
   justify-items: center;
`;

export const EmptyTitle = styled.h2`
   margin: 0;
   font-size: 16px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const EmptyText = styled.p`
   margin: 0;
   max-width: 460px;
   font-size: 13px;
   line-height: 1.6;
   color: #8a8a8a;
`;

// ─── Tiles (dobra 1) ──────────────────────────────────────────────────────

export const TileGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(206px, 1fr));
   gap: 14px;
`;

export const Tile = styled.div`
   display: grid;
   align-content: start;
   gap: 6px;
   min-height: 118px;
   padding: 16px;
   border: 1px solid ${CARD_BORDER};
   border-radius: 12px;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   box-shadow: ${CARD_SHADOW};
`;

export const TileLabel = styled.span`
   font-size: 12px;
   font-weight: 800;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

// Números grandes ficam com algarismo PROPORCIONAL: `tabular-nums` em display
// afrouxa o espaçamento e faz o valor parecer solto. Alinhamento tabular é para
// coluna de tabela, onde os dígitos se empilham.
export const TileValue = styled.strong`
   font-size: 23px;
   font-weight: 800;
   letter-spacing: -0.02em;
   white-space: nowrap;
   color: ${({ $danger, theme }) =>
      $danger ? '#b71c1c' : theme?.colors?.textPrimary ?? '#111'};
`;

export const TileSub = styled.span`
   font-size: 12px;
   line-height: 1.45;
   color: #8a8a8a;
`;

// ─── Barra de aging (dobra 1) ─────────────────────────────────────────────

// A separação entre faixas é feita por ESPAÇO na cor da superfície (2px), não
// por borda: borda adiciona tinta que não é dado. Por isso `gap` no flex e
// nenhum `overflow: hidden` no trilho — recortar o trilho cortaria os rótulos
// das pontas.
export const AgingTrack = styled.div`
   display: flex;
   gap: 2px;
   height: 34px;
   border-radius: 10px;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
`;

export const AgingSegment = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   /* Piso de 3px: na carteira real os buckets do meio valem ~2% cada e sem
      isso virariam fios invisíveis — a fatia existe, tem que aparecer. */
   min-width: 3px;
   background: ${({ $color }) => $color};
   color: ${({ $ink }) => $ink};
   font-size: 11px;
   font-weight: 800;
   ${numericCss}

   &:first-child {
      border-radius: 10px 0 0 10px;
   }

   &:last-child {
      border-radius: 0 10px 10px 0;
   }

   &:only-child {
      border-radius: 10px;
   }
`;

// Destaque do bucket 90+.
//
// Numa escala linear ele some dentro da barra: a carteira real é 80% "a vencer",
// então o núcleo duro (66% do VALOR vencido) vira uma faixa estreita. O número
// ao lado é o que impede o olho de subestimá-lo.
export const AgingHighlight = styled.div`
   display: grid;
   gap: 2px;
   padding: 10px 14px;
   border-radius: 10px;
   border: 1px solid ${({ theme }) => theme?.colors?.dangerBorder ?? 'rgba(171, 32, 32, 0.35)'};
   background: ${({ theme }) => theme?.colors?.dangerSoft ?? 'rgba(171, 32, 32, 0.08)'};
   text-align: right;
`;

export const AgingHighlightLabel = styled.span`
   font-size: 11px;
   font-weight: 800;
   color: #b71c1c;
`;

export const AgingHighlightValue = styled.strong`
   font-size: 18px;
   font-weight: 800;
   letter-spacing: -0.02em;
   white-space: nowrap;
   color: #b71c1c;
`;

export const AgingLegend = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
   gap: 10px;
   margin-top: 14px;
`;

export const LegendItem = styled.div`
   display: grid;
   gap: 3px;
   padding: 10px 12px;
   border: 1px solid ${CARD_BORDER};
   border-radius: 10px;
`;

export const LegendKey = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 7px;
   font-size: 11px;
   font-weight: 800;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};

   &::before {
      content: '';
      width: 9px;
      height: 9px;
      border-radius: 3px;
      background: ${({ $color }) => $color};
      flex-shrink: 0;
   }
`;

export const LegendValue = styled.strong`
   font-size: 16px;
   font-weight: 800;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   ${numericCss}
`;

export const LegendCount = styled.span`
   font-size: 11px;
   color: #8a8a8a;
   ${numericCss}
`;

// ─── Qualidade do crédito (dobra 2) ───────────────────────────────────────

export const MetricGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(232px, 1fr));
   gap: 14px;
`;

export const MetricBlock = styled.div`
   display: grid;
   align-content: start;
   gap: 8px;
   padding: 14px;
   border: 1px solid rgba(0, 0, 0, 0.07);
   border-radius: 12px;
`;

export const MetricLabel = styled.span`
   font-size: 12px;
   font-weight: 800;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

export const MetricValueRow = styled.p`
   margin: 0;
   display: flex;
   align-items: baseline;
   gap: 6px;
`;

export const MetricValue = styled.strong`
   font-size: 28px;
   font-weight: 800;
   letter-spacing: -0.02em;
   white-space: nowrap;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const MetricUnit = styled.span`
   font-size: 12px;
   color: #8a8a8a;
`;

export const MeterTrack = styled.div`
   height: 8px;
   border-radius: 999px;
   background: rgba(0, 0, 0, 0.07);
   overflow: hidden;
`;

export const MeterFill = styled.div`
   height: 100%;
   border-radius: 999px;
   background: ${({ $color }) => $color};
`;

// ─── Desfecho do que já venceu (dobra 2) ──────────────────────────────────
//
// Part-to-whole com denominador único: os títulos do recorte cujo vencimento já
// ocorreu se dividem em pago em dia · pago em atraso · segue em aberto.
// Azul = pago (dois tons), quente = não pago. É essa separação de MATIZ que
// carrega a leitura — não a intensidade.
export const OutcomeBlock = styled.div`
   display: grid;
   gap: 10px;
   padding: 14px;
   margin-bottom: 14px;
   border: 1px solid rgba(0, 0, 0, 0.07);
   border-radius: 12px;
`;

export const OutcomeHeadline = styled.p`
   margin: 0;
   font-size: 13px;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};

   strong {
      font-size: 20px;
      font-weight: 800;
      letter-spacing: -0.02em;
      white-space: nowrap;
   }
`;

export const OutcomeTrack = styled.div`
   display: flex;
   gap: 2px;
   height: 28px;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   border-radius: 8px;
`;

export const OutcomeSegment = styled.div`
   min-width: 3px;
   background: ${({ $color }) => $color};

   &:first-child {
      border-radius: 8px 0 0 8px;
   }

   &:last-child {
      border-radius: 0 8px 8px 0;
   }

   &:only-child {
      border-radius: 8px;
   }
`;

export const OutcomeLegend = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 14px;
`;

export const OutcomeKey = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 7px;
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   ${numericCss}

   &::before {
      content: '';
      width: 9px;
      height: 9px;
      border-radius: 3px;
      background: ${({ $color }) => $color};
      flex-shrink: 0;
   }
`;

export const MetricDenominator = styled.span`
   font-size: 11px;
   line-height: 1.5;
   color: #8a8a8a;
`;

// ─── Agenda de vencimentos (dobra 3) ──────────────────────────────────────

export const ForecastGrid = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 2fr) minmax(230px, 1fr);
   gap: 16px;

   @media (max-width: 760px) {
      grid-template-columns: 1fr;
   }
`;

export const ForecastRows = styled.div`
   display: grid;
   gap: 12px;
   align-content: center;
`;

// Barras LADO A LADO, nunca empilhadas: os horizontes são cumulativos (o de 30
// inclui o de 7), então empilhar somaria o mesmo dinheiro três vezes.
export const ForecastRow = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
`;

export const ForecastLabel = styled.span`
   width: 96px;
   flex-shrink: 0;
   font-size: 12px;
   font-weight: 800;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

// O trilho é `relative` porque o tooltip da linha é ancorado nele.
export const ForecastTrack = styled.div`
   position: relative;
   flex: 1 1 auto;
   min-width: 0;
   height: 30px;
   border-radius: 8px;
   background: rgba(0, 0, 0, 0.05);
`;

// Empilhamento recebido + pendente. O gap de 2px é a superfície separando as
// fatias, como na barra de aging — nunca borda.
export const ForecastStack = styled.div`
   display: flex;
   gap: 2px;
   height: 100%;
   width: ${({ $width }) => `${$width}%`};
   border-radius: 8px;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
`;

export const ForecastFill = styled.div`
   /* Piso de 3px: quem paga antecipado paga contas pequenas, então o segmento
      "recebido" chega a 2,6% do valor na janela de 30 dias. A fatia existe e
      precisa aparecer, senão o zero e o quase-zero viram a mesma coisa. */
   min-width: ${({ $share }) => ($share > 0 ? '3px' : '0')};
   height: 100%;
   background: ${({ $color, theme }) =>
      $color ?? theme?.colors?.primary ?? '#4098ef'};

   &:first-child {
      border-radius: 8px 0 0 8px;
   }

   &:last-child {
      border-radius: 0 8px 8px 0;
   }

   &:only-child {
      border-radius: 8px;
   }
`;

// Tooltip da linha — mesma linguagem do tooltip do gráfico de safra.
export const RowTooltip = styled.div`
   position: absolute;
   bottom: calc(100% + 8px);
   z-index: 3;
   pointer-events: none;
   padding: 9px 11px;
   border-radius: 10px;
   background: rgba(31, 31, 24, 0.96);
   color: #fff;
   box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
   font-size: 11.5px;
   line-height: 1.5;
   text-align: left;
   white-space: nowrap;

   strong {
      display: block;
      font-size: 12.5px;
      font-weight: 800;
      margin-bottom: 4px;
   }
`;

// Três campos por linha — status · valor · contagem — em colunas alinhadas.
// Alinhar em grade (e não com `space-between`) é o que faz os valores das
// linhas ficarem um sobre o outro e comparáveis de relance.
export const TooltipGrid = styled.div`
   display: grid;
   grid-template-columns: 1fr auto auto;
   gap: 3px 14px;
   align-items: center;
`;

export const TooltipLabel = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 7px;
   color: rgba(255, 255, 255, 0.78);

   ${({ $color }) =>
      $color
         ? `
   &::before {
      content: '';
      flex-shrink: 0;
      width: 8px;
      height: 8px;
      border-radius: 2px;
      background: ${$color};
   }`
         : ''}
`;

export const TooltipValue = styled.span`
   font-weight: 700;
   text-align: right;
   ${numericCss}
`;

export const TooltipCount = styled.span`
   color: rgba(255, 255, 255, 0.66);
   text-align: right;
   ${numericCss}
`;

export const ForecastLegend = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 14px;
   margin-top: 12px;
`;

export const ForecastValueCol = styled.div`
   width: 150px;
   flex-shrink: 0;
   display: grid;
   gap: 2px;
   text-align: right;
`;

export const ForecastValue = styled.strong`
   font-size: 15px;
   font-weight: 800;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   ${numericCss}
`;

export const ForecastCount = styled.span`
   font-size: 11px;
   color: #8a8a8a;
   ${numericCss}
`;

export const OverduePanel = styled.div`
   display: grid;
   align-content: center;
   gap: 6px;
   padding: 16px;
   border-radius: 12px;
   border: 1px solid rgba(211, 47, 47, 0.35);
   background: rgba(211, 47, 47, 0.07);
`;

export const OverdueLabel = styled.span`
   font-size: 12px;
   font-weight: 800;
   color: #b71c1c;
`;

export const OverdueValue = styled.strong`
   font-size: 26px;
   font-weight: 800;
   letter-spacing: -0.02em;
   white-space: nowrap;
   color: #b71c1c;
`;

export const OverdueCount = styled.span`
   font-size: 12px;
   color: #8a8a8a;
   ${numericCss}
`;

export const OverdueNote = styled.p`
   margin: 4px 0 0;
   font-size: 12px;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

// ─── Breakdown (dobra 4) ──────────────────────────────────────────────────

// A tabela é larga: em tela estreita ela rola SOZINHA, dentro do próprio card,
// sem empurrar a página para o lado.
export const TableWrap = styled.div`
   overflow-x: auto;
   margin: 0 -16px;
   padding: 0 16px;
`;

export const DataTable = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 12px;

   thead tr {
      background: ${({ theme }) => theme?.colors?.appBackground ?? '#373737'};
   }

   th {
      padding: 10px 12px;
      font-size: 11px;
      font-weight: 800;
      color: #fff;
      text-align: right;
      white-space: nowrap;
   }

   th:first-child {
      padding-left: 16px;
      text-align: left;
   }

   th:last-child {
      padding-right: 16px;
      text-align: left;
   }

   tbody tr {
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
   }

   /* Realce de leitura da linha. NÃO é affordance de clique: esta tabela não
      navega para lugar nenhum (não há drill-down para a listagem), então nada
      de cursor: pointer prometendo um gesto que não existe. */
   tbody tr:hover {
      background: #eaf3fd;
   }

   td {
      padding: 11px 12px;
      text-align: right;
      ${numericCss}
   }

   td:first-child {
      padding-left: 16px;
      text-align: left;
      font-weight: 700;
      white-space: normal;
   }

   td:last-child {
      padding-right: 16px;
      text-align: left;
   }
`;

// Linha cinza: "Outros" e "Sem classificação" não são categorias de verdade,
// são o resto. O fundo mais escuro as separa do top N sem escondê-las.
export const MutedRow = styled.tr`
   background: #f7f7f8;
`;

export const RowSub = styled.span`
   display: block;
   margin-top: 2px;
   font-size: 11px;
   font-weight: 500;
   color: #8a8a8a;
`;

export const OverdueCell = styled.span`
   font-weight: 700;
   color: ${({ $hasValue }) => ($hasValue ? '#b71c1c' : '#8a8a8a')};
`;

export const ShareCell = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   min-width: 180px;
`;

export const ShareTrack = styled.div`
   flex: 1 1 auto;
   height: 8px;
   border-radius: 999px;
   background: rgba(0, 0, 0, 0.06);
   overflow: hidden;
`;

export const ShareFill = styled.div`
   height: 100%;
   border-radius: 999px;
   background: ${({ $color, theme }) =>
      $color ?? theme?.colors?.primary ?? '#4098ef'};
`;

export const SharePct = styled.span`
   width: 52px;
   flex-shrink: 0;
   text-align: right;
   ${numericCss}
`;

// Recarga de um bloco que JÁ tem conteúdo (trocar a aba do breakdown, mudar o
// limiar da safra): o conteúdo anterior fica esmaecido no lugar em vez de virar
// skeleton. Skeleton em refetch pisca e faz a página pular — o operador perde a
// linha que estava lendo.
export const Refetching = styled.div`
   opacity: ${({ $busy }) => ($busy ? 0.45 : 1)};
   transition: opacity 140ms ease;
`;

export const InfoStrip = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   padding: 12px 16px;
   margin: 0 -16px;
   background: #fcfcfd;
   border-top: 1px solid rgba(0, 0, 0, 0.07);
   border-bottom: 1px solid rgba(0, 0, 0, 0.07);
   font-size: 12px;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};

   svg {
      flex-shrink: 0;
      color: #2f7bd8;
   }
`;

export const TableFooter = styled.div`
   padding: 12px 16px;
   margin: 0 -16px -16px;
   background: #fcfcfd;
   border-top: 1px solid rgba(0, 0, 0, 0.07);
   border-radius: 0 0 12px 12px;
   font-size: 12px;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

// ─── Safra / vintage (dobra 5) ────────────────────────────────────────────

export const VintageGrid = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 1fr) minmax(240px, 300px);
   gap: 16px;
   align-items: start;

   @media (max-width: 860px) {
      grid-template-columns: 1fr;
   }
`;

// Os rótulos de eixo são HTML posicionado ABSOLUTAMENTE sobre o SVG, em
// porcentagem do viewBox — assim o texto mantém tamanho e nitidez enquanto o
// desenho escala com o card. `<text>` dentro do SVG encolheria junto.
export const ChartFrame = styled.div`
   position: relative;
   width: 100%;

   svg {
      display: block;
      width: 100%;
      height: auto;
   }
`;

// Tooltip do ponto de safra. HTML sobre o SVG (mesma técnica dos rótulos de
// eixo): mantém tipografia nítida enquanto o desenho escala, e permite layout
// de texto que `<text>` não daria.
export const ChartTooltip = styled.div`
   position: absolute;
   z-index: 2;
   pointer-events: none;
   min-width: 148px;
   padding: 8px 10px;
   border-radius: 10px;
   background: rgba(31, 31, 24, 0.96);
   color: #fff;
   box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
   font-size: 11.5px;
   line-height: 1.45;

   strong {
      display: block;
      font-size: 12.5px;
      font-weight: 800;
      margin-bottom: 2px;
   }

   span {
      display: block;
      color: rgba(255, 255, 255, 0.72);
      font-variant-numeric: tabular-nums;
   }
`;

// Transição do ponto sob o cursor. `r` e `stroke-width` são propriedades
// geométricas animáveis por CSS em SVG2 — quem não suportar simplesmente troca
// de tamanho sem interpolar, que é degradação aceitável.
export const ChartMarks = styled.g`
   circle {
      transition: r 120ms ease, stroke-width 120ms ease;
   }

   @media (prefers-reduced-motion: reduce) {
      circle {
         transition: none;
      }
   }
`;

export const AxisLabel = styled.span`
   position: absolute;
   font-size: 11px;
   color: #8a8a8a;
   white-space: nowrap;
   pointer-events: none;
   ${numericCss}
`;

export const ChartCaption = styled.p`
   margin: 6px 0 0;
   font-size: 11px;
   color: #b0b0b0;
`;

export const VintageLegend = styled.div`
   display: grid;
   gap: 6px;
   align-content: start;
`;

export const LegendButton = styled.button`
   display: flex;
   align-items: center;
   gap: 8px;
   width: 100%;
   padding: 7px 9px;
   border-radius: 9px;
   border: 1px solid
      ${({ $active }) =>
         $active ? 'rgba(64, 152, 239, 0.5)' : 'rgba(0, 0, 0, 0.08)'};
   background: ${({ $active }) => ($active ? '#f3f8fe' : '#fff')};
   font-size: 12px;
   text-align: left;
   cursor: pointer;
   opacity: ${({ $dimmed }) => ($dimmed ? 0.45 : 1)};
   transition: background 140ms ease, border-color 140ms ease, opacity 140ms ease;

   &:hover {
      background: #f3f8fe;
   }

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }
`;

// Traço da legenda: 16px na cor da linha, tracejado quando a safra é carga
// histórica — a mesma marca visual que a linha usa no gráfico.
export const LegendDash = styled.span`
   flex-shrink: 0;
   width: 16px;
   height: 0;
   border-top: 2px ${({ $dashed }) => ($dashed ? 'dashed' : 'solid')}
      ${({ $color }) => $color};
`;

export const LegendSafra = styled.span`
   flex: 1 1 auto;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

export const LegendTitulos = styled.span`
   color: #8a8a8a;
   ${numericCss}
`;

export const HeatTable = styled.table`
   width: 100%;
   border-collapse: separate;
   border-spacing: 4px;
   font-size: 12px;

   th {
      padding: 6px 8px;
      font-size: 11px;
      font-weight: 800;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      text-align: right;
      white-space: nowrap;
   }

   th:first-child {
      text-align: left;
   }

   td {
      padding: 0 8px;
      text-align: right;
      ${numericCss}
   }

   td:first-child {
      padding: 0;
      text-align: left;
      white-space: nowrap;
   }
`;

export const HeatCell = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   height: 38px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 800;
   border: 1px solid transparent;
   ${numericCss}
`;

// Ponto que ainda não ocorreu: hachura + traço, nunca zero. A diferença visual
// tem que ser de NATUREZA (não há dado), não de intensidade (dado baixo).
export const HeatCellEmpty = styled(HeatCell)`
   background: repeating-linear-gradient(
      135deg,
      #fafafa 0 6px,
      #f2f2f2 6px 12px
   );
   border: 1px dashed rgba(0, 0, 0, 0.12);
   color: #b5b5b5;
`;

// Linha do heatmap que está plotada no gráfico. O realce liga as duas
// superfícies: o operador vê na tabela quais safras a curva acima representa.
export const HeatRow = styled.tr`
   background: ${({ $selected }) => ($selected ? '#f3f8fe' : 'transparent')};
`;

// O gesto de incluir/tirar a safra do gráfico é um BOTÃO de verdade, não um
// `onClick` na linha: precisa de foco, teclado e estado anunciável.
export const SafraToggle = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 8px;
   padding: 5px 9px;
   border-radius: 8px;
   border: 1px solid
      ${({ $selected }) =>
         $selected ? 'rgba(64, 152, 239, 0.5)' : 'transparent'};
   background: ${({ $selected }) => ($selected ? '#fff' : 'transparent')};
   font-size: 12px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   cursor: pointer;
   transition: background 140ms ease, border-color 140ms ease;

   &:hover:not(:disabled) {
      background: #eaf3fd;
   }

   &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
   }

   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(64, 152, 239, 0.18);
   }
`;

export const SafraCell = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

export const SafraBadge = styled.span`
   padding: 2px 7px;
   border-radius: 999px;
   font-size: 10px;
   font-weight: 800;
   background: ${({ $historical }) =>
      $historical ? 'rgba(0, 0, 0, 0.06)' : 'rgba(2, 136, 209, 0.1)'};
   border: 1px solid
      ${({ $historical }) =>
         $historical ? 'rgba(0, 0, 0, 0.12)' : 'rgba(2, 136, 209, 0.3)'};
   color: ${({ $historical }) => ($historical ? '#444' : '#01579b')};
`;

// ─── Estado de erro por bloco ─────────────────────────────────────────────
//
// Erro NUNCA é silencioso e NUNCA derruba a tela inteira: o bloco que falhou
// diz o que houve (com o código de suporte em 5xx) e os outros seguem vivos.
export const BlockError = styled.div`
   display: grid;
   gap: 8px;
   justify-items: start;
   padding: 16px;
   border-radius: 10px;
   background: ${({ theme }) =>
      theme?.colors?.dangerSoft ?? 'rgba(171, 32, 32, 0.08)'};
   border: 1px solid
      ${({ theme }) => theme?.colors?.dangerBorder ?? 'rgba(171, 32, 32, 0.35)'};
   font-size: 13px;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

// Placeholder das dobras que ainda não têm conteúdo montado.
export const BlockPlaceholder = styled.div`
   min-height: ${({ $h }) => `${$h ?? 120}px`};
`;
